import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Container } from "react-bootstrap";
import classes from "../WebsiteManagement/websiteManagement.module.css";
import Page from "../pagination/pagination";
import {
  GetAdminHolidayBookings,
  DownloadBookingTicket,
  DownloadBookingInvoice,
} from "../../entities/action/action";

const PAGE_SIZE = 50;

const thStyle = {
  padding: "10px 6px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  borderBottom: "2px solid #ccc",
  whiteSpace: "nowrap",
};
const tdStyle = {
  padding: "10px 6px",
  fontSize: 12,
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};

const formatAmount = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `INR ${n}`;
};

const HolidayBookings = () => {
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await GetAdminHolidayBookings();
        if (cancelled) return;
        if (res?.success && Array.isArray(res.bookings)) {
          setAll(res.bookings);
        } else {
          setAll([]);
          setLoadError(res?.message || "Failed to load holiday bookings.");
        }
      } catch {
        if (!cancelled) {
          setAll([]);
          setLoadError("Failed to load holiday bookings.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((b) => {
      const name = String(b.name || "").toLowerCase();
      const phone = String(b.phone || "").toLowerCase();
      const ref = String(b.bookingRef || "").toLowerCase();
      const pkg = String(b.packageName || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || ref.includes(q) || pkg.includes(q);
    });
  }, [all, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Fragment>
      <Container style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
        <form onSubmit={(e) => e.preventDefault()} style={{ display: "flex", alignItems: "stretch", width: "50vw" }}>
          <input
            className={classes.SearchInput}
            placeholder="Search by name, phone, ref, or package"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="button" className={classes.SearchButton} onClick={() => setSearch(searchInput)}>
            Search
          </button>
        </form>
      </Container>
      {loading ? (
        <p>Loading holiday bookings…</p>
      ) : loadError ? (
        <p>{loadError}</p>
      ) : pageItems.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
            <thead>
              <tr style={{ backgroundColor: "#585858", color: "#fff" }}>
                <th style={thStyle}>Booking Date & Time</th>
                <th style={thStyle}>Reference ID</th>
                <th style={thStyle}>Package Name</th>
                <th style={thStyle}>Travel Date</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Travellers</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => {
                const orderDate = item?.orderDate ? new Date(item.orderDate) : null;
                const duration =
                  item.totalDays > 0
                    ? `${item.totalDays}D / ${item.totalNights || 0}N`
                    : null;
                return (
                  <tr key={item.bookingId} style={{ backgroundColor: "#f9f9f9" }}>
                    <td style={tdStyle}>
                      {orderDate && !Number.isNaN(orderDate.getTime()) ? (
                        <>
                          {orderDate.getDate()}/{orderDate.getMonth() + 1}/{orderDate.getFullYear()}
                          <div style={{ color: "gray" }}>{orderDate.toLocaleTimeString()}</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={tdStyle}>{item.bookingRef || "—"}</td>
                    <td style={tdStyle}>
                      {item.packageName || "—"}
                      {duration && <div style={{ color: "gray" }}>{duration}</div>}
                    </td>
                    <td style={tdStyle}>{item.travelDate || "—"}</td>
                    <td style={tdStyle}>{item.route || "—"}</td>
                    <td style={tdStyle}>{item.travellers != null ? item.travellers : "—"}</td>
                    <td style={tdStyle}>
                      <div>{item.name || "—"}</div>
                      <div style={{ color: "gray" }}>{item.phone || "—"}</div>
                    </td>
                    <td style={tdStyle}>{formatAmount(item.amount)}</td>
                    <td style={tdStyle}>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        style={{ fontSize: 10, marginBottom: 5, whiteSpace: "nowrap" }}
                        onClick={() => DownloadBookingTicket(item.bookingId)}
                      >
                        Download Ticket
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        style={{ fontSize: 10, whiteSpace: "nowrap" }}
                        onClick={() =>
                          DownloadBookingInvoice(
                            item.bookingId,
                            `Holiday_Invoice_${item.bookingRef || item.bookingId}.pdf`
                          )
                        }
                      >
                        Download Invoice
                      </Button>
                      <div
                        style={{
                          background: "#0dcaf0",
                          padding: 4,
                          marginTop: 5,
                          textAlign: "center",
                          fontSize: 10,
                        }}
                      >
                        {(item?.status || "").toUpperCase()}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No holiday bookings found</p>
      )}
      {totalPages > 1 && <Page count={totalPages} page={page} change={(_, v) => setPage(v)} />}
    </Fragment>
  );
};

export default HolidayBookings;
